import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { SvgIconComponent } from "../../../shared/components/svg-icon/svg-icon.component";
import { Router } from "@angular/router";
import { MediaService } from "../../../services/api/media/media.service";
import { ImageListItemComponent } from "../../../shared/components/image-list-item/image-list-item.component";
import { TranslateModule } from "@ngx-translate/core";

import { Image } from "../../../models/ui/image";
import { RepairRequest } from "../../../models/api/fault/repairRequest";
import { Media } from "../../../models/api/media";
import { first } from "rxjs";
import { createTimeoutSignal } from "../../../shared/utils/timeout.utils";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../shared/components/timeout/timeout.component";

@Component({
  selector: "app-repair-details",
  standalone: true,
  imports: [SvgIconComponent, ImageListItemComponent, TranslateModule, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./repair-details.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./repair-details.component.scss",
})
export class RepairDetailsComponent implements OnInit {
  public images: Image[] = [];
  public loadingMedia = signal(false);
  public timeoutMedia = createTimeoutSignal(this.loadingMedia);
  private contextData = window.history.state;
  public repairRequest: RepairRequest = this.contextData.repairRequest;
  private router = inject(Router);
  private mediaService = inject(MediaService);

  /**
   * Initializes the component by fetching associated media for the repair request.
   * If the repair request has a valid ID, it retrieves media files from the media service
   * and populates the `images` array with details such as link, label, and timestamp.
   */
  public ngOnInit() {
    if (this.repairRequest.id) {
      this.loadingMedia.set(true);
      this.mediaService
        .getMedia("REPAIR_REQUEST", this.repairRequest.id, "REPAIR_ATTACHMENT")
        .pipe(first())
        .subscribe({
          next: (media: Media[]) => {
            if (media) {
              media.forEach((mediaFile: Media) => {
                const label = mediaFile.additionalInfo?.label;
                const link = mediaFile.url;
                const id = mediaFile.id;
                const timestamp = mediaFile.timestamp?.created_at;
                if (label && link && id && timestamp) {
                  this.images.push({
                    link: link,
                    labelDisplay: label,
                    labelValue: label,
                    uploadTimestamp: timestamp,
                    id: id,
                  });
                }
              });
            }
            this.loadingMedia.set(false);
          },
        });
    }
  }
  /**
   * Navigate back to the Repairs page.
   */
  public goBack() {
    this.router.navigate(["/dashboard/repairs"]);
  }
  /**
   * Opens a given link in a new tab, allowing the user to download the associated file.
   * @param link The URL of the file to download.
   */
  public downloadFile(link: string) {
    window.open(link, "_blank");
  }
}
